import React, { useState, useRef } from "react";
import {
  Sparkles,
  Upload,
  FileText,
  ExternalLink,
  Loader2,
  Lock,
  Shield,
  X,
  Bookmark,
  BookmarkCheck,
} from "lucide-react";
import { matchResume, isDemo, uploadResumeFile, trackEvent } from "@/lib/api";
import { demoMatches } from "@/lib/demoData";
import { companyLogo, companyInitials } from "@/lib/companyLogo";
import { isSaved, toggleSaved, logApplication } from "@/lib/storage";
import { requestLead } from "@/components/dashboard/LeadGateModal";

function ScoreRing({ score }) {
  const r = 22;
  const c = 2 * Math.PI * r;
  const off = c - (Math.min(100, Math.max(0, score)) / 100) * c;
  const color = score >= 85 ? "#00FF66" : score >= 70 ? "#FFC400" : "#E10600";
  return (
    <svg width="54" height="54" viewBox="0 0 54 54" className="shrink-0">
      <circle cx="27" cy="27" r={r} stroke="#0a0a0a14" strokeWidth="5" fill="none" />
      <circle
        cx="27"
        cy="27"
        r={r}
        stroke={color}
        strokeWidth="5"
        fill="none"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={off}
        transform="rotate(-90 27 27)"
        style={{ transition: "stroke-dashoffset 600ms cubic-bezier(.2,.7,.2,1)" }}
      />
      <text x="27" y="32" textAnchor="middle" fontFamily="Archivo Black" fontSize="14" fill="#0a0a0a">
        {score}
      </text>
    </svg>
  );
}

function MatchRow({ m, onApply }) {
  const [saved, setSavedState] = useState(isSaved({ url: m.apply_url }));
  return (
    <div className="bg-white border-2 border-[#0a0a0a] shadow-brutal-sm p-4 flex items-start gap-4" data-testid="match-row">
      <ScoreRing score={m.score || 0} />
      <div className="flex-1 min-w-0">
        <div className="font-display text-base leading-tight">{m.title}</div>
        <div className="font-mono-body text-[12px] text-[#0a0a0a]/70 mt-0.5">
          {m.company} · {m.location || "—"}
        </div>
        <p className="font-mono-body text-[12px] text-[#0a0a0a]/80 mt-2">{m.reason}</p>
      </div>
      <div className="flex flex-col items-end gap-2">
        <button
          onClick={() => {
            const next = toggleSaved({ url: m.apply_url, title: m.title, company: m.company, location: m.location });
            setSavedState(next);
          }}
          aria-label={saved ? "Unsave" : "Save"}
          className={`w-9 h-9 border-2 border-[#0a0a0a] shadow-brutal-sm inline-flex items-center justify-center hover-pop ${
            saved ? "bg-[#FFC400]" : "bg-white"
          }`}
        >
          {saved ? <BookmarkCheck size={14} strokeWidth={3} /> : <Bookmark size={14} strokeWidth={3} />}
        </button>
        <button
          onClick={() => onApply(m)}
          className="hover-pop bg-[#E10600] text-white border-2 border-[#0a0a0a] px-3 py-2 font-accent text-[11px] shadow-brutal-sm inline-flex items-center gap-1.5"
        >
          Apply <ExternalLink size={11} strokeWidth={3} />
        </button>
      </div>
    </div>
  );
}

export default function DashboardMatch() {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setError(null);
    // Plain-text → read locally, no server round-trip.
    if (f.type === "text/plain" || f.name.toLowerCase().endsWith(".txt")) {
      const t = await f.text();
      setText(t);
      return;
    }
    // PDF / DOC / DOCX → POST to /resume/upload for parsing, then drop the
    // returned text into the textarea so the user can verify before scoring.
    if (isDemo()) {
      setText(`[Uploaded ${f.name} — demo placeholder resume text]`);
      return;
    }
    setUploading(true);
    const res = await uploadResumeFile(f);
    setUploading(false);
    if (!res?.ok) {
      if (res?.status === 401 || res?.error === "login_required") {
        setError("Sign in to upload — your resume parses on jobhacki.com and never leaves the call.");
      } else {
        setError("Couldn't parse that file. Try a smaller PDF, or paste your resume text below.");
      }
      return;
    }
    const parsed = res.text || res.resume_text || res.content || "";
    if (!parsed.trim()) {
      setError("We parsed the file but didn't find any readable text. Paste your resume below instead.");
      return;
    }
    setText(parsed);
  };

  const run = async () => {
    if (text.trim().length < 40) {
      setError("Paste at least a few lines of your resume — we need real content to score against.");
      return;
    }
    setError(null);
    trackEvent("match.run.click", { len: text.length });
    // Email-gate the Match action (skipped if we've already captured them).
    const gate = await requestLead({
      source: "match",
      label: "Score your resume against live ATS jobs",
      meta: { len: text.length },
    });
    if (!gate.ok) return;
    setBusy(true);
    let res;
    if (isDemo()) res = demoMatches(text);
    else res = await matchResume({ resume_text: text });
    setResult(res);
    setBusy(false);
    trackEvent("match.run.done", { count: res?.matches?.length || 0, ok: !!res?.ok });
  };

  const apply = (m) => {
    logApplication({ url: m.apply_url, title: m.title, company: m.company, location: m.location }, "applied");
    window.open(m.apply_url, "_blank", "noopener,noreferrer");
  };

  return (
    <div data-testid="dash-match" className="space-y-6 max-w-5xl">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl md:text-4xl flex items-center gap-3">
            <Sparkles className="text-[#E10600]" /> RESUME MATCH
          </h1>
          <p className="font-mono-body text-sm text-[#0a0a0a]/65 mt-1.5 max-w-xl">
            Paste your resume → ranked openings with a 0–100 Job Match Score and one-line reason.
          </p>
        </div>
        <span className="inline-flex items-center gap-2 font-mono-body text-[11px] bg-[#FFC400]/30 border-2 border-[#0a0a0a] px-2 py-1">
          <Shield size={11} strokeWidth={3} /> Resume never stored
        </span>
      </div>

      <div className="bg-white border-4 border-[#0a0a0a] shadow-brutal p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div className="font-accent text-[11px]">YOUR RESUME</div>
          <div className="flex items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept=".txt,.pdf,.doc,.docx"
              onChange={onFile}
              className="hidden"
              data-testid="match-upload-input"
            />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="match-upload-btn"
              className="hover-pop bg-white border-2 border-[#0a0a0a] px-3 py-2 font-accent text-[11px] inline-flex items-center gap-1.5 shadow-brutal-sm disabled:opacity-60"
            >
              {uploading ? (
                <>
                  <Loader2 size={11} className="animate-spin" /> Parsing PDF…
                </>
              ) : (
                <>
                  <Upload size={11} strokeWidth={3} /> Upload PDF/Word
                </>
              )}
            </button>
            {text && (
              <button
                onClick={() => setText("")}
                className="hover-pop bg-white border-2 border-[#0a0a0a] px-2.5 py-2 font-accent text-[11px] inline-flex items-center gap-1.5 shadow-brutal-sm"
              >
                <X size={11} strokeWidth={3} /> Clear
              </button>
            )}
          </div>
        </div>
        <textarea
          data-testid="match-textarea"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={10}
          placeholder="Paste your resume text here — name, roles, bullets, tools, links."
          className="w-full bg-[#FDFBF7] border-2 border-[#0a0a0a] p-3 font-mono-body text-[12px] focus:outline-none focus:bg-white"
        />
        {error && (
          <div className="mt-2 font-mono-body text-[12px] text-[#E10600]">{error}</div>
        )}
        <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
          <div className="font-mono-body text-[11px] text-[#0a0a0a]/55">
            {text.length} characters
          </div>
          <button
            onClick={run}
            disabled={busy}
            data-testid="match-run"
            className="hover-pop bg-[#E10600] text-white border-2 border-[#0a0a0a] px-4 py-2.5 font-accent text-sm shadow-brutal-sm inline-flex items-center gap-2"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} strokeWidth={3} />}
            Match my resume
          </button>
        </div>
      </div>

      {result?.ok === false && (
        <div className="bg-white border-4 border-[#0a0a0a] shadow-brutal p-6 text-center">
          <Lock className="mx-auto text-[#E10600]" />
          <div className="font-display text-lg mt-3">Log in on jobhacki.com to run a real match.</div>
          <p className="font-mono-body text-sm text-[#0a0a0a]/65 mt-1.5">
            Matching uses your JobHacki session. Sign in once, then refresh this page.
          </p>
          <a
            href="https://jobhacki.com/login"
            target="_blank"
            rel="noopener noreferrer"
            className="hover-pop mt-4 inline-flex items-center gap-2 bg-[#E10600] text-white border-2 border-[#0a0a0a] px-4 py-2.5 font-accent text-sm shadow-brutal-sm"
          >
            Open login on jobhacki.com <ExternalLink size={12} strokeWidth={3} />
          </a>
        </div>
      )}

      {result?.ok && result.matches?.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="font-display text-xl">YOUR TOP MATCHES</div>
            <div className="font-mono-body text-[11px] text-[#0a0a0a]/55">
              Scored {result.matches.length} live roles
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3">
            {result.matches.map((m, i) => (
              <MatchRow key={m.apply_url || i} m={m} onApply={apply} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
